export type FastPoseMetric = {
    sourceTime: number;
    centerY: number;
    torsoAngle: number;
    bboxAspect: number;
    hipY: number;
};

export type FastFallGateResult = {
    candidate: boolean;
    riskScore: number;
    signalCount: number;
    signals: string[];
    posture: 'stand' | 'sit' | 'kneel' | 'lie' | 'transition';
    evaluatedAtVideoTimeMs: number;
};

export function estimateFastPosture(metric: FastPoseMetric, previous?: FastPoseMetric): FastFallGateResult['posture'] {
    const changing = previous && (
        Math.abs(metric.centerY - previous.centerY) >= 0.055
        || Math.abs(metric.torsoAngle - previous.torsoAngle) >= 18
        || Math.abs(metric.bboxAspect - previous.bboxAspect) >= 0.22
    );
    if (changing) return 'transition';
    if (metric.bboxAspect >= 1.02 || (metric.torsoAngle >= 58 && metric.hipY >= 0.58)) return 'lie';
    if (metric.hipY >= 0.66 && metric.torsoAngle < 58) return 'kneel';
    if (metric.centerY >= 0.57 || metric.hipY >= 0.62) return 'sit';
    return 'stand';
}

export function evaluateFastFallGate(samples: FastPoseMetric[]): FastFallGateResult {
    const latest = samples[samples.length - 1];
    if (!latest) return {
        candidate: false, riskScore: 0, signalCount: 0, signals: [], posture: 'stand', evaluatedAtVideoTimeMs: 0,
    };
    const recent = samples.filter(item => latest.sourceTime - item.sourceTime <= 0.55);
    const baseline = recent[0] || latest;
    const previous = recent.length >= 2 ? recent[recent.length - 2] : undefined;
    const span = Math.max(0.001, latest.sourceTime - baseline.sourceTime);
    const centerDrop = latest.centerY - baseline.centerY;
    const hipDrop = latest.hipY - baseline.hipY;
    const torsoChange = Math.abs(latest.torsoAngle - baseline.torsoAngle);
    const aspectChange = latest.bboxAspect - baseline.bboxAspect;
    const verticalToHorizontal = baseline.bboxAspect < 0.82 && latest.bboxAspect >= 0.96;
    const descentVelocity = centerDrop / span;
    const signals: string[] = [];
    if (centerDrop >= 0.065 || descentVelocity >= 0.30) signals.push('rapid_center_drop');
    if (torsoChange >= 20) signals.push('torso_tilt_change');
    if (verticalToHorizontal || aspectChange >= 0.24) signals.push('vertical_to_horizontal');
    if (hipDrop >= 0.055 && latest.hipY >= 0.60) signals.push('hip_floor_approach');
    if (latest.centerY >= 0.68 && latest.hipY >= 0.72) signals.push('low_posture');
    const enoughFrames = recent.length >= 2 && span >= 0.07;
    const candidate = enoughFrames && signals.length >= 2;
    const riskScore = Math.max(0, Math.min(0.94,
        0.24 + signals.length * 0.115 + Math.max(0, centerDrop) * 1.15 + Math.max(0, torsoChange - 10) / 180
    ));
    return {
        candidate,
        riskScore: candidate ? Math.max(0.50, riskScore) : Math.min(0.49, riskScore),
        signalCount: signals.length,
        signals,
        posture: estimateFastPosture(latest, previous),
        evaluatedAtVideoTimeMs: Math.max(0, latest.sourceTime * 1000),
    };
}
